import { useCallback, useEffect, useState } from "react";
import {
  LiveKitRoom,
  RoomAudioRenderer,
  GridLayout,
  ParticipantTile,
  ControlBar,
  useTracks,
  useRoomContext,
  LayoutContextProvider,
  useCreateLayoutContext,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import "@livekit/components-styles";
import { ErrorBoundary } from "./ErrorBoundary";

type LiveKitCredentials = {
  serverUrl: string;
  token: string;
  roomName: string;
};

function EnableDevicesOnJoin({ onWarn }: { onWarn: (msg: string) => void }) {
  const room = useRoomContext();
  useEffect(() => {
    if (!room) return;
    void (async () => {
      try {
        await room.localParticipant.setCameraEnabled(true);
        await room.localParticipant.setMicrophoneEnabled(true);
      } catch (e) {
        console.warn("[LiveKit] device enable failed", e);
        onWarn("Allow camera and microphone in the browser, then use the controls below.");
      }
    })();
  }, [room, onWarn]);
  return null;
}

function StageGrid() {
  const layoutContext = useCreateLayoutContext();
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false },
  );

  if (tracks.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-ink-400">
        Waiting for participants… turn on your camera with the button below.
      </div>
    );
  }

  return (
    <LayoutContextProvider value={layoutContext}>
      <GridLayout tracks={tracks} className="lk-grid lk-stage-grid gap-2 p-2">
        <ParticipantTile />
      </GridLayout>
    </LayoutContextProvider>
  );
}

export function LiveKitStage({
  meetingId,
  authToken,
  inviteToken,
  guestName,
}: {
  meetingId: string;
  authToken?: string | null;
  inviteToken?: string;
  guestName?: string;
}) {
  const [creds, setCreds] = useState<LiveKitCredentials | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [roomError, setRoomError] = useState<string | null>(null);
  const warnDevices = useCallback((msg: string) => setRoomError(msg), []);

  useEffect(() => {
    let cancelled = false;
    setTokenError(null);
    setRoomError(null);
    setCreds(null);
    void (async () => {
      try {
        const headers: Record<string, string> = { "Content-Type": "application/json" };
        if (authToken) headers.Authorization = `Bearer ${authToken}`;
        const res = await fetch("/api/livekit/token", {
          method: "POST",
          headers,
          body: JSON.stringify({ meetingId, inviteToken, guestName }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "LiveKit token failed");
        if (!cancelled) {
          setCreds({
            serverUrl: data.serverUrl,
            token: data.token,
            roomName: data.roomName,
          });
        }
      } catch (e) {
        if (!cancelled) setTokenError(e instanceof Error ? e.message : "LiveKit error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [meetingId, authToken, inviteToken, guestName]);

  if (tokenError) {
    return (
      <div className="flex min-h-[420px] flex-col items-center justify-center gap-2 rounded-2xl bg-red-500/10 p-6 text-sm text-red-200 ring-1 ring-red-400/20">
        <p className="font-medium">LiveKit could not connect</p>
        <p className="text-center text-xs text-red-200/80">{tokenError}</p>
      </div>
    );
  }

  if (!creds) {
    return (
      <div className="flex min-h-[420px] items-center justify-center rounded-2xl bg-surface-900/50 text-ink-300 ring-1 ring-white/10">
        Connecting to LiveKit…
      </div>
    );
  }

  return (
    <ErrorBoundary label="LiveKit error">
      <div className="relative flex min-h-[420px] flex-col overflow-hidden rounded-2xl ring-1 ring-white/10 lg:min-h-[min(72vh,780px)]">
        {roomError ? (
          <div className="absolute inset-x-0 top-0 z-10 bg-amber-500/25 px-3 py-2 text-center text-xs text-amber-50">
            {roomError}
          </div>
        ) : null}
        <LiveKitRoom
          token={creds.token}
          serverUrl={creds.serverUrl}
          connect
          audio
          video
          options={{
            adaptiveStream: true,
            dynacast: true,
            videoCaptureDefaults: { resolution: { width: 1280, height: 720 } },
          }}
          className="lk-bridgecall-room flex min-h-[420px] flex-1 flex-col bg-surface-900"
          onError={(err) => {
            console.error("[LiveKit]", err);
            setRoomError(err.message);
          }}
          onMediaDeviceFailure={() =>
            setRoomError("Camera or microphone blocked — check browser permissions.")
          }
        >
          <EnableDevicesOnJoin onWarn={warnDevices} />
          <RoomAudioRenderer />
          <div className="lk-stage-body relative min-h-[280px] flex-1">
            <StageGrid />
          </div>
          <div className="border-t border-white/10 bg-surface-900/95 p-2">
            <ControlBar
              controls={{
                microphone: true,
                camera: true,
                screenShare: true,
                leave: false,
                chat: false,
                settings: false,
              }}
            />
          </div>
        </LiveKitRoom>
      </div>
    </ErrorBoundary>
  );
}
